import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { authAPI } from '../services/api'
import toast from 'react-hot-toast'
import { Loader2, Mail, ArrowLeft } from 'lucide-react'

const ForgotPassword = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [email, setEmail] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await authAPI.forgotPassword(email)
      setIsSubmitted(true)
      toast.success(t('auth.resetEmailSent'))
    } catch (error) {
      // Don't show error to prevent email enumeration
      setIsSubmitted(true)
    } finally {
      setIsLoading(false)
    }
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-blue-500 mb-2">{t('app.title')}</h1>
            <p className="text-gray-400">{t('app.subtitle')}</p>
          </div>

          <div className="bg-gray-800 rounded-lg p-8 text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mb-4">
                <Mail className="h-8 w-8 text-blue-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">{t('auth.checkEmail')}</h2>
              <p className="text-gray-400">
                {t('auth.resetEmailInfo')}
              </p>
            </div>

            <Link to="/login" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg flex items-center justify-center transition-colors">
              <ArrowLeft className="mr-2 h-5 w-5" />
              {t('auth.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-blue-500 mb-2">{t('app.title')}</h1>
          <p className="text-gray-400">{t('app.subtitle')}</p>
        </div>

        <div className="bg-gray-800 rounded-lg p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">{t('auth.forgotPasswordTitle')}</h2>
            <p className="text-gray-400">{t('auth.forgotPasswordDescription')}</p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                {t('auth.email')}
              </label>
              <input
                id="email"
                type="email"
                required
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white py-3 px-4 rounded-lg flex items-center justify-center transition-colors"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-5 w-5" />
                  {t('common.loading')}
                </>
              ) : (
                t('auth.sendResetLink')
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-blue-500 hover:text-blue-400 flex items-center justify-center">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('auth.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPassword
